/**
 * Egy helymérés MENNYIRE hihető — közös küszöbök a térképnek
 * (`useSharedPosition`) és az időjárás-widgetnek (`weatherPlace.ts`).
 *
 * MIÉRT KELL? Az asztali böngésző nem GPS-ből tájékozódik, hanem Wi-Fi- és
 * IP-alapú becslésből. Ez Budapesten is kilométereket téved, rosszabb
 * esetben egy másik megyét ad (mérve, GRUNDO #61: XI. kerület helyett
 * „Mándok"). A telefon jobb mérése ilyenkor a megosztott
 * `users/{uid}/private/position` dokumentumban van.
 */

/** Ennél pontosabb saját mérésnél nem kell más forrás. */
export const GOOD_ENOUGH_M = 150;
/** Ennél pontatlanabb mérés (IP-alapú becslés) semmire nem jó. */
export const USELESS_ABOVE_M = 3_000;
/** Ennél régebbi megosztott helyzet elavult. */
export const SHARED_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * Ennyi elmozdulás fölött a pontos új mérés akkor is felülírja a tárolt
 * helyzetet, ha nem pontosabb nála (GRUNDO #61). #86 (Geri): 1 km → 100 m —
 * 1 km-en belül az asztali nézet egy napig a régi helyet mutatta.
 */
export const MOVED_FAR_M = 100;

export interface PositionFix {
  lat: number;
  lng: number;
  accuracyM: number;
  /** Mérés ideje (epoch ms). */
  at: number;
  /**
   * Who measured it (#86): `device` = the phone app's GPS, `browser` = a web
   * page's geolocation. Missing on older stored positions.
   */
  source?: PositionSource;
}

export type PositionSource = 'device' | 'browser';

/**
 * Felírja-e a saját mérést a megosztott `private/position` dokumentumba.
 *
 * Eddig két eset írt: a PONTOSABB mérés, és a 24 óránál régebbi tárolt
 * helyett bármi használható. A HIBA (GRUNDO #61): reggel Budapesten 5 m-es
 * mérés, délután Debrecenben 10 m-es — a tárolt helyzet egy napig Budapest
 * maradt, mert a debreceni mérés „nem volt pontosabb". A harmadik eset ezt
 * zárja: a pontos (≤ 150 m) mérés, ami több mint 1 km-re van, felülír.
 *
 * A pontossági feltétel véd a fordított hibától: egy asztali IP-becslés
 * (kilométeres pontatlanság) sosem írhatja felül a telefon jó mérését.
 */
export function shouldPublishPosition(
  own: PositionFix,
  shared: PositionFix | null,
  now: number,
  distance: (a: PositionFix, b: PositionFix) => number,
): boolean {
  /*
    ⚠️ A BÖNGÉSZŐ NEM ÍR (Geri, #86): a desktop WiFi-becslése magabiztos
    pontosságot is jelent („40 m”), miközben kilométereket téved — a fenti
    pontossági védelem ezt nem fogta meg, és a rossz fix felülírta a telefonét.
  */
  if (own.source === 'browser') return false;
  if (own.accuracyM > USELESS_ABOVE_M) return false;
  if (shared === null) return true;
  /* A korábban böngészőből írt (megbízhatatlan) helyzetet a telefon mindig cseréli. */
  if (shared.source === 'browser') return true;
  if (own.accuracyM < shared.accuracyM) return true;
  if (now - shared.at > SHARED_MAX_AGE_MS) return true;
  /*
    A GPS-zaj nem elmozdulás: az eltérés a két mérés pontatlanságánál is legyen
    nagyobb, különben egy 140 m-es fix felülírhatna egy 5 m-est puszta szórásból.
  */
  const moved = distance(own, shared);
  return own.accuracyM <= GOOD_ENOUGH_M && moved > MOVED_FAR_M && moved > own.accuracyM + shared.accuracyM;
}

/**
 * Hova álljon a térkép. A telefonon a saját GPS-e az igazság (a megosztott
 * csak akkor, ha az pontosabb és friss). A WEBEN a telefon utolsó ismert
 * helye nyer, bármilyen régi — Geri, #86: a böngésző „random helyre rakott”.
 */
export function choosePosition<T extends PositionFix>(
  own: T | null,
  shared: T | null,
  native: boolean,
  now: number,
): T | null {
  if (!native) {
    if (shared && shared.source !== 'browser') return shared;
    return own ?? shared;
  }
  if (own && own.accuracyM <= GOOD_ENOUGH_M) return own;
  if (shared && now - shared.at <= SHARED_MAX_AGE_MS) {
    if (!own || shared.accuracyM < own.accuracyM) return shared;
  }
  return own;
}
